import type { OutputData } from './types';
import { getPriorityColor, translatePriority } from './types';
import { WerkcurveLadder } from './WerkcurveTab';
import { WERKCURVE_LEVELS } from '@/lib/interview/outputs/werkcurve-levels';

interface OverviewTabProps {
  data: OutputData;
  onOpenTab?: (tab: 'werkcurve' | 'use_cases' | 'plan') => void;
}

export function OverviewTab({ data, onOpenTab }: OverviewTabProps) {
  const w = data.werkcurve;

  return (
    <div className="space-y-6">
      {/* Their question */}
      {w?.question && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <p className="text-sm text-off-black/50 mb-1">Jullie vraag</p>
          <p className="font-display font-light text-2xl text-off-black leading-snug">{w.question}</p>
        </div>
      )}

      {/* Why this matters now */}
      {w?.stakes && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <h2 className="font-display font-light text-2xl text-off-black mb-4">Waarom dit nu speelt</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <p className="text-xs font-medium text-off-black/45 uppercase tracking-wide mb-1">Wat er veranderd is</p>
              <p className="text-off-black/75">{w.stakes.why_now}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-off-black/45 uppercase tracking-wide mb-1">Als het over een jaar nog zo gaat</p>
              <p className="text-off-black/75">{w.stakes.cost_of_waiting}</p>
            </div>
          </div>
          {w.stakes.quote && (
            <blockquote className="mt-5 pl-3 border-l-2 border-orange-cta text-off-black/55 italic">
              {w.stakes.quote}
            </blockquote>
          )}
        </div>
      )}

      {/* Executive Summary */}
      {data.requirements?.executive_summary && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <h2 className="font-display font-light text-2xl text-off-black mb-4">Managementsamenvatting</h2>
          <p className="text-off-black/70 leading-relaxed whitespace-pre-wrap">
            {data.requirements.executive_summary}
          </p>
        </div>
      )}

      {/* Where their work sits on the werkcurve */}
      {w && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
            <h2 className="font-display font-light text-2xl text-off-black">
              Waar jullie werk nu zit: {w.level} · {WERKCURVE_LEVELS[w.level].name}
            </h2>
          </div>
          <WerkcurveLadder level={w.level} />
          <p className="text-off-black/70 mt-5">{WERKCURVE_LEVELS[w.level].who}</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm no-print">
            <button type="button" onClick={() => onOpenTab?.('werkcurve')} className="underline underline-offset-4 decoration-off-black/20 text-off-black/70 hover:text-off-black">
              Waarom we dit denken
            </button>
            {w.use_cases.length > 0 && (
              <button type="button" onClick={() => onOpenTab?.('use_cases')} className="underline underline-offset-4 decoration-off-black/20 text-off-black/70 hover:text-off-black">
                {w.use_cases.length} kansrijke use cases
              </button>
            )}
          </div>
        </div>
      )}

      {/* Something they may not see themselves */}
      {w?.insight && (
        <div className="bg-off-black text-off-white rounded-2xl p-6">
          <p className="text-sm text-off-white/55 mb-2">Wat ons opviel</p>
          <p className="font-display font-light text-xl leading-snug">{w.insight}</p>
        </div>
      )}

      {/* The one use case we'd start with */}
      {w?.recommendation && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <p className="text-sm text-off-black/50 mb-1">Waar we zouden beginnen</p>
          <h2 className="font-display font-light text-2xl text-off-black">{w.recommendation.title}</h2>
          <p className="text-off-black/70 mt-2">{w.recommendation.why_first}</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm no-print">
            <button type="button" onClick={() => onOpenTab?.('use_cases')} className="underline underline-offset-4 decoration-off-black/20 text-off-black/70 hover:text-off-black">
              Zo pak je het aan
            </button>
            {(w.route?.length ?? 0) > 0 && (
              <button type="button" onClick={() => onOpenTab?.('plan')} className="underline underline-offset-4 decoration-off-black/20 text-off-black/70 hover:text-off-black">
                De komende 90 dagen
              </button>
            )}
          </div>
        </div>
      )}

      {/* One step for this week */}
      {w?.next_step && (
        <div className="rounded-2xl p-6 bg-orange-cta/10 border border-orange-cta/20">
          <h2 className="font-display font-light text-2xl text-off-black mb-2">Jullie stap voor deze week</h2>
          <p className="text-off-black/80">{w.next_step}</p>
        </div>
      )}

      {/* Key Challenges */}
      {data.requirements?.challenges && data.requirements.challenges.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-off-black/10">
          <h2 className="font-display font-light text-2xl text-off-black mb-4">Belangrijkste knelpunten</h2>
          <div className="space-y-3">
            {data.requirements.challenges.slice(0, 3).map((challenge, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(challenge.priority)}`}>
                  {translatePriority(challenge.priority)}
                </span>
                <p className="text-off-black/70 flex-1">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}


    </div>
  );
}
